import { AxiosResponse } from 'axios';
import { t } from 'i18next';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { extractPagination } from '~services/pagination';

export class TooManyRecordsError extends Error {}

interface Page<TItem> {
  data: TItem[];
  pagination?: { after?: string };
}

const pageQuery = (cursor: string | undefined, limit: number) => ({
  'page[limit]': limit,
  ...(cursor ? { 'page[after]': cursor } : {}),
});

export type PageQuery = ReturnType<typeof pageQuery>;

interface FetchAllPagesOptions {
  maxRecords?: number;
  pageSize?: number;
  signal?: AbortSignal;
}

export default async function fetchAllPages<TItem>(
  fetchPage: (
    cursor: string | undefined,
    limit: number,
    signal?: AbortSignal,
  ) => Promise<Page<TItem> | undefined>,
  {
    maxRecords = Infinity,
    pageSize = MAX_API_PAGE_SIZE,
    signal,
  }: FetchAllPagesOptions = {},
) {
  const items: TItem[] = [];
  let cursor: string | undefined;

  for (let pageCount = 0; pageCount * pageSize < maxRecords; pageCount += 1) {
    signal?.throwIfAborted();
    const page = await fetchPage(cursor, pageSize, signal);
    items.push(...(page?.data ?? []));
    cursor = page?.pagination?.after;

    if (!cursor) {
      return items;
    }
  }

  throw new TooManyRecordsError(
    t('error_messages.too_many_records', { max: maxRecords }),
  );
}

export const parseApiPage = <TItem, TRecord>(
  response: AxiosResponse<{ data: TItem[] }>,
  parse: (item: TItem) => TRecord,
) => ({
  data: response.data.data.map((item) => parse(item)),
  pagination: extractPagination(response),
});

export function fetchAllApiPages<TItem, TRecord>(
  request: (
    query: PageQuery,
    signal?: AbortSignal,
  ) => Promise<AxiosResponse<{ data: TItem[] }>>,
  parse: (item: TItem) => TRecord,
  options?: FetchAllPagesOptions,
) {
  return fetchAllPages(
    async (cursor, limit, signal) =>
      parseApiPage(await request(pageQuery(cursor, limit), signal), parse),
    options,
  );
}
